import {
  Button,
  Card,
  Checkbox,
  Col,
  Input,
  Row,
  Select,
  Skeleton,
  Table,
  Tooltip,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useTheme } from "../../Context/ThemeContext";
import { useClient } from "../../Context/ClientContext";
import { ColumnsType } from "antd/es/table";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  LedgerStatus,
  LedgerStatusDropDown,
} from "../../Services/LedgerService";
import { notificationContext } from "../../Common/Routing/PageRoute";
import OrganisationsService, {
  IEmpData,
} from "../../Services/Organisations/OrganisationsService";
import { OrgDataType } from "./Organisations";
import { IApiResponse } from "../../../Utilities/ApiUtility.axios";
import { IIdNameModel } from "../../Services/ItemsService";
import { ConfirmationModal } from "../../Common/ConfirmationModal";
import AddEmpModel from "./AddEmpModel";
import { tablePageSizeOptions } from "../../Common/ThemeSetting";

interface EmpDataType extends OrgDataType {
  empName: string;
  empCode?: string;
  fatherName?: string;
  email?: string;
  createLoginCredential: boolean;
  subUnitName: string;
  depttName: string;
  designationName: string;
  seniorEmpId?: string;
}

const Employees = () => {
  const { currentClient } = useClient();
  // const [openEmpModel, setOpenEmpModel] = useState<{
  //   open: boolean;
  //   id: string;
  // }>({
  //   open: false,
  //   id: "",
  // });
  // const [deleteConfirmation, setDeleteConfirmation] = useState<{
  //   open: boolean;
  //   id: string;
  // }>({
  //   open: false,
  //   id: "",
  // });
  const [openModel, setOpenModel] = useState<{ type: string; param: any }>({
    type: "",
    param: {},
  });
  const [addUpdateTrigger, setAddUpdateTrigger] = useState<boolean>(false);
  const [searchEmp, setSearchEmp] = useState<string>("");
  const { themeData } = useTheme();
  const { openNotification } = useContext(notificationContext);
  const [empList, setEmpList] = useState<EmpDataType[]>([]);
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(
    themeData?.tablePageSize ?? 15
  );
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [filter, setfilter] = useState<any>({
    sortCol: "",
    sortDir: "",
  });
  const [trigger, setTrigger] = useState<boolean>(false);
  const [ledgerStatus, setLedgerStatus] = useState<LedgerStatus>(
    LedgerStatus.active
  );

  const getEmpList = async () => {
    setDataLoading(true);
    await OrganisationsService.getEmployeesList(
      currentClient?.id!,
      (page - 1) * pageSize,
      pageSize,
      searchEmp,
      filter.sortCol,
      filter.sortDir,
      ledgerStatus
    ).then((val) => {
      if (val?.items) {
        setEmpList(
          val.items?.map<EmpDataType>((emp: IEmpData, index: number) => {
            return {
              sno: emp.sno,
              id: emp.id as string,
              empName: emp.empName,
              empCode: emp.empCode,
              email: emp.email,
              fatherName: emp.fatherName,
              seniorEmpId: emp.seniorEmpId,
              createLoginCredential: emp.createLoginCredential,
              orgName: emp.orgName as string,
              subUnitName: emp.subUnitName as string,
              depttName: emp.depttName as string,
              designationName: emp.designationName as string,
              status: emp.status!,
            };
          }) || []
        );
        setTotalRecords(val?.totalRecords);
      }
    });
    setDataLoading(false);
  };

  const handleDelete = async (selId: string) => {
    if (selId) {
      await OrganisationsService.deleteEmployee(currentClient?.id!, selId)
        .then((val: IApiResponse<IIdNameModel>) => {
          if (val?.status) {
            openNotification(
              "success",
              val?.result?.name + " employee deleted successfully"
            );
            setAddUpdateTrigger((prev) => !prev);
          } else {
            openNotification("error", val?.errors && val?.errors[0]?.message);
          }
        })
        .catch((err) => {
          openNotification("error", err?.message || "");
        });
      setOpenModel({
        type: "",
        param: {},
      });
    }
  };

  useEffect(() => {
    if (currentClient?.id) {
      getEmpList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentClient?.id,
    addUpdateTrigger,
    searchEmp,
    ledgerStatus,
    trigger,
    page,
    pageSize,
    filter,
  ]);

  // --------- Departments Table Columns  ---------
  const depttCols: ColumnsType<EmpDataType> = [
    {
      title: "S.No.",
      dataIndex: "sno",
      key: "sno",
      width: "6%",
      render: (value: number) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Employee's Name",
      dataIndex: "empName",
      sorter: true,
      defaultSortOrder: "ascend",
      width: "14%",
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      sorter: true,
      width: "14%",
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Father's Name",
      dataIndex: "fatherName",
      width: "14%",
      sorter: true,
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Designation",
      dataIndex: "designationName",
      width: "14%",
      sorter: true,
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Department",
      dataIndex: "depttName",
      width: "14%",
      sorter: true,
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Sub Unit",
      dataIndex: "subUnitName",
      width: "14%",
      sorter: true,
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    // {
    //   title: "Organisation Name",
    //   dataIndex: "orgName",
    //   render: (value: string) => (
    //     <Skeleton
    //       active
    //       loading={dataLoading}
    //       paragraph={{ rows: 1 }}
    //       title={false}
    //     >
    //       {value}
    //     </Skeleton>
    //   ),
    // },
    {
      title: "Action",
      dataIndex: "id",
      width: "10%",
      render: (_, record) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              {record.status !== LedgerStatus.deleted && (
                <Tooltip
                  title={
                    record?.status === LedgerStatus.active
                      ? "In-Active"
                      : "Active"
                  }
                  placement="top"
                  color="#3D4998"
                >
                  <Checkbox
                    checked={record?.status !== LedgerStatus.deactive}
                    title="Delete"
                    onChange={(e: any) => {
                      // console.log(e.target.checked, record);
                      setOpenModel({
                        type: "isActive",
                        param: {
                          name: record.empName,
                          id: record.id,
                          itemStatus: record?.status,
                        },
                      });
                      // setSelectChange(record?.isActive ? "In-Active" : "Active");
                    }}
                  />
                </Tooltip>
              )}
            </div>
            <div>
              {record.status === LedgerStatus.active ? (
                <Tooltip title="Edit" placement="top" color="#3D4998">
                  <EditOutlined
                    style={{
                      color: "#444653",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setOpenModel({
                        type: "addEmployee",
                        param: {
                          id: record.id,
                        },
                      });
                    }}
                  />
                </Tooltip>
              ) : (
                <></>
              )}
            </div>
            <div>
              <Tooltip title="Delete" placement="top" color="red">
                <DeleteOutlined
                  style={{
                    color: "red",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    setOpenModel({
                      type: "delete",
                      param: {
                        id: record.id,
                        name: record.empName,
                      },
                    })
                  }
                />
              </Tooltip>
            </div>
          </div>
        </Skeleton>
      ),
    },
  ];

  //----- Table Ascending & Desending Order -----
  const tableSort = (pagination?: any, sort?: any, filter?: any) => {
    setfilter({
      sortCol: filter.column !== undefined ? filter.columnKey : "name",
      sortDir:
        filter.column !== undefined
          ? filter.order === "descend"
            ? "desc"
            : "asc"
          : "desc",
    });
    // console.log("sort1111111111111", sort?.TaxRate?.Rate, sort);
  };
  return (
    <Card
      size="small"
      className="TableCard"
      style={{
        marginTop: 35,
        boxShadow: "0px 0px 10px 0px #96A1C84D",
      }}
    >
      <div>
        <Row justify="space-between">
          <Col>
            <Row gutter={15}>
              <Col>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() =>
                    setOpenModel({
                      type: "addEmployee",
                      param: {},
                    })
                  }
                >
                  Employee
                </Button>
              </Col>
            </Row>
          </Col>
          <Col>
            <Row gutter={8}>
              <Col>
                <Input
                  allowClear
                  style={{ width: "220px" }}
                  placeholder="Search.."
                  onChange={(e: any) => {
                    setDataLoading(true);
                    if (!e.cancelable) {
                      const searchItemGroup = setTimeout(() => {
                        setSearchEmp(e.target.value);
                      }, 1000);
                      return () => clearTimeout(searchItemGroup);
                    } else {
                      // console.log("Click cancle............");
                      setSearchEmp(e.target.value);
                    }
                  }}
                  suffix={dataLoading || searchEmp ? "" : <SearchOutlined />}
                />
              </Col>
              <Col>
                <Select
                  style={{ width: "110px" }}
                  defaultValue={LedgerStatus.active}
                  onSelect={(e) => {
                    setLedgerStatus(e);
                    // setExportItemsData([]);
                    setPage(1);
                  }}
                  options={LedgerStatusDropDown}
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <Table
          className={`Tabel-style table-${
            themeData?.componentSize ?? "middle"
          }`}
          columns={depttCols}
          locale={{
            triggerDesc: "",
            triggerAsc: "",
            cancelSort: "",
          }}
          onChange={tableSort}
          dataSource={
            dataLoading
              ? (Array.from({ length: pageSize }, (_, index) => ({
                  key: `loading-${index}`,
                })) as EmpDataType[])
              : empList
          }
          pagination={{
            total: totalRecords,
            current: page,
            pageSize: pageSize,
            showSizeChanger: true,
            onChange: (page: number, pageSize: number) => {
              setPage(page);
              setPageSize(pageSize);
              // setExportType(-1);
            },
            pageSizeOptions: tablePageSizeOptions,
            showTotal: (totalRecords, page) =>
              `${page[0]}-${page[1]} of ${totalRecords} items`,
          }}
        />
      </div>
      {openModel.type === "addEmployee" && (
        <AddEmpModel
          isModalOpen={true}
          setIsModalOpen={() =>
            setOpenModel({
              type: "",
              param: {},
            })
          }
          id={openModel.param?.id}
          setTrigger={setAddUpdateTrigger}
        />
      )}
      {openModel.type === "delete" && (
        <ConfirmationModal
          open={true}
          onNo={() =>
            setOpenModel({
              type: "",
              param: {},
            })
          }
          onYes={() => handleDelete(openModel.param?.id)}
          text={`Are you sure you want to delete this row "${openModel.param?.name}" ?`}
        />
      )}
      {openModel.type === "isActive" && (
        <ConfirmationModal
          open={true}
          onNo={() => setOpenModel({ type: "", param: "" })}
          onYes={async () => {
            try {
              const apiData = await OrganisationsService.isActiveEmployee(
                currentClient?.id!,
                openModel.param?.id,
                openModel.param?.itemStatus === LedgerStatus.deactive
                  ? LedgerStatus.active
                  : LedgerStatus.deactive
              );
              if (apiData.result) {
                openNotification(
                  "success",
                  `Department "${openModel?.param?.name}" ${
                    openModel?.param?.itemStatus === LedgerStatus.deactive
                      ? "Active"
                      : "InActive"
                  } sucessfully.`
                );

                setTrigger((x) => !x);
                setOpenModel({ type: "", param: "" });
              } else {
                openNotification("error", apiData?.message);
                console.log("error", apiData?.message);
                setOpenModel({ type: "", param: "" });
              }
            } catch (err) {
              openNotification("error", err);
              console.log("error", err);
              setOpenModel({ type: "", param: "" });
            }
          }}
          text={`Are you sure you want to ${
            openModel?.param?.itemStatus === LedgerStatus.deactive
              ? "Active"
              : "InActive"
          }  this "${openModel?.param?.name}" Department. ?`}
        />
      )}
    </Card>
  );
};

export default Employees;
